/**
 * Lexiun JWT token management for LexPortal.
 *
 * Updated 2026-04-30 auth-flow pivot: both LexPortal and LexIDE go through
 * the canonical `POST /api/auth/exchange` endpoint hosted at lexserver
 * (was `/portal/api/auth/exchange` on lexruntime pre-pivot). This module
 * is the LexPortal-side counterpart of `lexide/src/lib/lexiun-jwt.ts` —
 * same shape, same caching strategy, same Clerk-bridge pattern.
 *
 * Dev workflows that don't run Clerk can paste a dev-login JWT into
 * `localStorage["lexportal.lexiunToken"]` as an escape hatch.
 */

const AUTH_EXCHANGE = "/api/auth/exchange";
const SEED_TOKEN_LS_KEY = "lexportal.lexiunToken";
// Path is `/lexserver/...` (not `/api/...`) so the Vite dev-server
// proxy forwards to lexserver:3200. Same convention PlatformApi uses
// for every other /api/platform call. AUTH_EXCHANGE above is the
// exception — it has its own dedicated proxy entry in vite.config.ts.
const DEV_PLATFORM_TOKEN_ENDPOINT =
  "/lexserver/api/platform/dev-platform-token";

let cachedToken: string | null = null;
let cachedScopes: string[] | null = null;
let tokenExpiry = 0;

/**
 * Decode the `scopes` claim from an unverified JWT payload.
 *
 * Safe because the token was just minted by lexserver's auth exchange on
 * the same origin (or pasted in via the dev escape hatch) — we trust it.
 * `scopes` defaults to `[]` so callers always get an array. The route-level
 * platform-admin gate in App.tsx uses `scopes.includes("platform.admin")`
 * to fence LexPortal off from non-platform-admin Clerk sessions that leak
 * in via cross-subdomain SSO with LexIDE.
 *
 * Exported for unit testing — the runtime path uses it via `getLexiunToken`.
 */
export function decodeJwtScopes(token: string): string[] {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return [];
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { scopes?: unknown };
    return Array.isArray(payload.scopes)
      ? payload.scopes.filter((s): s is string => typeof s === "string")
      : [];
  } catch {
    return [];
  }
}

/**
 * Module-level Clerk-token resolver. Set at app boot by
 * `<ClerkTokenBridge>` in App.tsx — bridges `useAuth().getToken()`
 * (a hook) into a plain function the non-React `getLexiunToken()` below
 * can call. `null` when no Clerk session is active.
 */
let clerkTokenResolver: (() => Promise<string | null>) | null = null;

export function setClerkTokenResolver(
  resolver: (() => Promise<string | null>) | null,
): void {
  clerkTokenResolver = resolver;
  clearAuthCache();
}

async function readSourceToken(): Promise<
  { kind: "seed"; token: string } | { kind: "clerk"; token: string } | null
> {
  if (typeof localStorage !== "undefined") {
    const seed = localStorage.getItem(SEED_TOKEN_LS_KEY);
    if (seed && seed.trim() !== "") return { kind: "seed", token: seed };
  }
  if (clerkTokenResolver) {
    const clerk = await clerkTokenResolver();
    if (clerk) return { kind: "clerk", token: clerk };
  }
  // Local-dev auto-seed: if we're not running with Clerk AND there's
  // no seed token in localStorage, hit the dev-only endpoint that
  // mints a PLATFORM_ADMIN token. Returns null in production builds
  // (where Clerk is enabled, the resolver above already fired and
  // returned a Clerk token) AND in dev when lexserver rejects with
  // 403/404 (NODE_ENV !== "development" on the server side). The
  // returned token is cached in localStorage so subsequent loads
  // don't re-mint until expiry.
  const autoSeed = await tryAutoSeedDevPlatformToken();
  if (autoSeed) return { kind: "seed", token: autoSeed };
  return null;
}

/**
 * One-shot fetch of the dev-only PLATFORM_ADMIN token endpoint. Stashes
 * the resulting token in localStorage so the second navigation skips
 * the round-trip via the seed branch above.
 *
 * Failure modes:
 *   - 403 / 404: lexserver is in production mode (route not mounted or
 *     handler rejects). Returns `null` — caller falls through to the
 *     "no auth source" path and PlatformApi sends an unauthenticated
 *     request, surfacing the 401 via the page's error banner. Same
 *     behaviour as before this auto-seed shipped.
 *   - Network error: lexserver isn't running. Returns `null`. The
 *     viewer surfaces the failure on the next API call rather than
 *     here (we don't have a good way to display this through the
 *     get-token surface).
 *   - 429: rate limited. Returns `null`. The dev-platform-token
 *     endpoint allows 30/min/IP — only relevant if the SPA is
 *     thrashing the endpoint, which would indicate a code bug.
 */
async function tryAutoSeedDevPlatformToken(): Promise<string | null> {
  if (typeof fetch === "undefined" || typeof localStorage === "undefined") {
    return null;
  }
  try {
    const res = await fetch(DEV_PLATFORM_TOKEN_ENDPOINT, { method: "POST" });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      ok?: boolean;
      data?: { token: string };
    };
    if (!body?.data?.token) return null;
    // Persist so the next page load takes the cheap localStorage path
    // instead of re-hitting the endpoint (which costs a Mongo audit
    // write per call).
    localStorage.setItem(SEED_TOKEN_LS_KEY, body.data.token);
    return body.data.token;
  } catch {
    return null;
  }
}

/**
 * Get a Lexiun JWT, exchanging via lexserver's `/api/auth/exchange` if
 * needed. Returns `null` (rather than throwing) when no auth source is
 * available — both PlatformApi and GatewayProvider handle the null case
 * gracefully (PlatformApi sends an unauthenticated request, GatewayProvider
 * falls back to its `lexdev` dev token).
 */
export async function getLexiunToken(): Promise<string | null> {
  const now = Date.now() / 1000;
  if (cachedToken && tokenExpiry > now + 60) {
    notifyAuth("ok");
    return cachedToken;
  }

  const source = await readSourceToken();
  if (!source) return null;

  if (source.kind === "seed") {
    cachedToken = source.token;
    cachedScopes = decodeJwtScopes(cachedToken);
    try {
      const parts = cachedToken.split(".");
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      ) as { exp?: number };
      tokenExpiry = payload.exp ?? now + 3600;
    } catch {
      tokenExpiry = now + 3600;
    }
    notifyAuth("ok");
    return cachedToken;
  }

  try {
    const res = await fetch(AUTH_EXCHANGE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${source.token}`,
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) {
      console.warn(`[lexportal-auth] exchange failed: ${res.status} ${res.statusText}`);
      return null;
    }
    // Phase A i18n (lexide-only, 2026-05-15) added a `locale: { user, org }`
    // field to the response; LexPortal silently ignores it — platform-admin
    // UI is English-only today. When LexPortal joins the i18n track (Phase D
    // of `ai-docs/lexiun-auth-guide.md` LLM-provider strategy section),
    // mirror lexide/src/lib/lexiun-jwt.ts:applyServerLocale here.
    const body = (await res.json()) as {
      ok?: boolean;
      data?: { token: string; expiresIn: number };
    };
    if (!body.data?.token) return null;
    cachedToken = body.data.token;
    cachedScopes = decodeJwtScopes(cachedToken);
    tokenExpiry = now + body.data.expiresIn;
    notifyAuth("ok");
    return cachedToken;
  } catch (err) {
    console.warn("[lexportal-auth] exchange error:", err);
    return null;
  }
}

/** Clear cached Lexiun JWT (called on Clerk identity swap or sign-out). */
export function clearAuthCache(): void {
  cachedToken = null;
  cachedScopes = null;
  tokenExpiry = 0;
}

/**
 * Resolve the caller's scopes from the cached Lexiun JWT. Triggers an
 * exchange if none is cached. Returns `[]` when no auth source is
 * available (rather than throwing) — callers default to "no permissions"
 * on a missing/malformed scopes claim. The route-level platform-admin
 * gate uses this to decide whether to render the LexPortal SPA shell.
 */
export async function getCurrentScopes(): Promise<string[]> {
  if (cachedScopes === null) {
    await getLexiunToken();
  }
  return cachedScopes ?? [];
}

// ── Session-recovery surface ─────────────────────────────────────────
//
// Before this, a server 401 ("Invalid or expired token") never cleared
// anything: the in-memory cache (and worse, a now-dead seed token in
// localStorage) was re-served forever → a permanent dead-end the user
// could only escape by manually clearing storage. `invalidateAuth()` is
// called by PlatformApi on a 401 so the NEXT call re-exchanges fresh
// (Clerk) or re-auto-seeds a fresh dev token, self-healing if the Clerk
// session is still valid. It also notifies subscribers so the UI can
// show an actionable "session expired" recovery surface instead of a
// raw error banner.

type AuthState = "expired" | "ok";
const authListeners = new Set<(s: AuthState) => void>();
let lastAuthState: AuthState = "ok";

function notifyAuth(s: AuthState): void {
  if (s === lastAuthState) return;
  lastAuthState = s;
  for (const cb of authListeners) cb(s);
}

/** Subscribe to auth-recovery state. Returns an unsubscribe fn. The
 *  callback fires immediately with the current state. */
export function subscribeAuthState(cb: (s: AuthState) => void): () => void {
  authListeners.add(cb);
  cb(lastAuthState);
  return () => authListeners.delete(cb);
}

/**
 * Hard-invalidate the session: drop the in-memory cache AND the dead
 * `localStorage` seed (the part `clearAuthCache` never touched — the
 * actual cause of the loop), then signal "expired". The next
 * `getLexiunToken()` falls through to a fresh Clerk exchange / dev
 * auto-seed; on success `getLexiunToken` clears the "expired" state.
 */
export function invalidateAuth(): void {
  clearAuthCache();
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(SEED_TOKEN_LS_KEY);
  }
  notifyAuth("expired");
}
